/* Presentation enhancements only. The existing fit quiz owns jacket / SKU decisions. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const putText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };

  function init(root) {
    if (!root || root.dataset.afInitialized) return;
    root.dataset.afInitialized = 'true';
    const section = root.closest('.shopify-section');
    const abort = new AbortController();
    const on = (el, event, fn, options = {}) => el?.addEventListener(event, fn, { ...options, signal: abort.signal });
    let raf = 0;
    let knownQuiz = null;

    function findFit(event) {
      event?.preventDefault();
      const target = root.querySelector('.fur-quiz') || root.querySelector('variant-picker') || root.querySelector('product-form');
      if (!target) return;
      target.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'center' });
      const focusTarget = target.querySelector('.quiz-step:not(.hidden) .quiz_ques') || target.querySelector('.quiz_heading') || target;
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    }

    function setupQuiz() {
      const quiz = root.querySelector('.fur-quiz');
      if (!quiz || quiz === knownQuiz) return;
      knownQuiz = quiz;
      for (const step of quiz.querySelectorAll('.quiz-step')) {
        const models = step.querySelector('.quiz_models');
        if (!models || models.children.length < 8 || step.querySelector('.af-model-search')) continue;
        const search = document.createElement('input');
        search.type = 'search';
        search.className = 'af-model-search input';
        search.placeholder = 'Search your jacket model';
        search.setAttribute('aria-label', 'Search jacket models');
        search.autocomplete = 'off';
        models.before(search);
        const filter = () => {
          const term = search.value.toLowerCase().trim();
          for (const button of models.querySelectorAll('.quiz-btn')) {
            const name = button.textContent.toLowerCase().trim();
            button.hidden = name !== 'other' && !name.includes(term);
          }
        };
        on(search, 'input', filter);
        on(quiz, 'click', (event) => {
          if (event.target.closest('.restart, [data-next]')) { search.value = ''; filter(); }
        });
      }
      on(quiz, 'click', (event) => {
        if (!event.target.closest('.quiz-btn, .restart')) return;
        queueMicrotask(() => {
          update();
          // Preserve natural tapping on touch; announce the new question for keyboard users.
          if (event.detail === 0) {
            const question = quiz.querySelector('.quiz-step:not(.hidden) .quiz_ques') || quiz.querySelector('.quiz-complete-message:not(.hidden)');
            question?.setAttribute('tabindex', '-1');
            question?.focus({ preventScroll: true });
          }
        });
      });
    }

    function update() {
      raf = 0;
      if (!root.isConnected) { observer.disconnect(); reviewObserver.disconnect(); abort.abort(); return; }
      setupQuiz();
      const quiz = root.querySelector('.fur-quiz');
      const complete = !quiz || quiz.dataset.afFitReady === 'true';
      root.dataset.afFitRequired = String(!complete);
      if (quiz) {
        quiz.dataset.afComplete = String(complete);
        const step = quiz.querySelector('.quiz-step:not(.hidden)');
        let stage = complete ? 3 : 1;
        let label = 'Your jacket';
        if (complete) label = 'Fit selected';
        else if (step && /q2-|newer-models-/.test(step.id)) { stage = 2; label = 'Jacket model'; }
        else if (step && !/q1-/.test(step.id)) { stage = 3; label = /custom-length/.test(step.id) ? 'Zipper length' : 'Jacket details'; }
        quiz.querySelectorAll('.af-quiz-progress > i').forEach((bar, index) => bar.classList.toggle('is-active', index < stage));
        putText(quiz.querySelector('[data-af-fit-step]'), label);
        let selection = quiz.querySelector('.af-fit-selection');
        if (!selection) {
          selection = document.createElement('p');
          selection.className = 'af-fit-selection';
          quiz.querySelector('.quiz-complete-message')?.after(selection);
        }
        const jacket = quiz.querySelector('[name="properties[Jacket]"]')?.value || '';
        const restored = quiz.querySelector('.quiz-restored-note:not(.hidden)');
        selection.hidden = !complete || !jacket || !!restored;
        putText(selection, jacket ? `Your jacket: ${jacket}` : '');
      }
      const sticky = section.querySelector('[data-af-sticky]');
      if (sticky) {
        sticky.dataset.afNeedsFit = String(!complete);
        const form = document.getElementById(root.dataset.afForm);
        const gallery = root.querySelector('product-gallery');
        const buyRect = form?.getBoundingClientRect();
        const galleryRect = gallery?.getBoundingClientRect();
        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')) || 80;
        const buyingVisible = buyRect && buyRect.bottom > headerHeight && buyRect.top < window.innerHeight - 70;
        const pastGallery = galleryRect ? galleryRect.bottom < headerHeight + 30 : root.getBoundingClientRect().top < -200;
        const footerRect = document.querySelector('.shopify-section--footer')?.getBoundingClientRect();
        const footerVisible = footerRect && footerRect.top < window.innerHeight;
        sticky.dataset.afVisible = String(pastGallery && !buyingVisible && !footerVisible);
      }
      updateGallery();
      updateRating();
    }

    function schedule() { if (!raf) raf = requestAnimationFrame(update); }

    function updateGallery() {
      const carousel = root.querySelector('.product-gallery__carousel');
      if (!carousel) return;
      const media = [...carousel.querySelectorAll('.product-gallery__media')].filter(node => !node.hidden);
      if (!media.length) return;
      const bounds = carousel.getBoundingClientRect();
      const centre = bounds.left + bounds.width / 2;
      let index = 0;
      let distance = Infinity;
      media.forEach((node, i) => {
        const rect = node.getBoundingClientRect();
        const next = Math.abs(rect.left + rect.width / 2 - centre);
        if (next < distance) { distance = next; index = i; }
      });
      putText(root.querySelector('[data-af-gallery-count]'), `${index + 1} / ${media.length}`);
    }

    function updateRating() {
      const link = root.querySelector('[data-af-review-link]');
      if (!link) return;
      const sources = [
        root.querySelector('[data-af-review-source] [data-average-rating][data-number-of-reviews]'),
        document.querySelector('.jdgm-rev-widg[data-average-rating][data-number-of-reviews]')
      ];
      for (const source of sources) {
        const rating = Number(source?.dataset.averageRating);
        const count = Number(source?.dataset.numberOfReviews);
        if (!(rating > 0 && rating <= 5 && Number.isInteger(count) && count > 0)) continue;
        const label = `${rating.toFixed(1)}/5 · ${count.toLocaleString()} ${count === 1 ? 'review' : 'reviews'}`;
        putText(link.querySelector('[data-af-rating-text]'), label);
        link.setAttribute('aria-label', `${label}. Read customer reviews`);
        link.querySelector('[data-af-rating-stars]').hidden = false;
        link.querySelector('[data-af-rating-fill]').style.width = `${rating * 20}%`;
        break;
      }
    }

    on(section, 'click', (event) => {
      if (event.target.closest('[data-af-find-fit]')) findFit(event);
      if (event.target.closest('[data-af-review-link], .jdgm-prev-badge') && document.getElementById('af-customer-reviews')) {
        event.preventDefault();
        document.getElementById('af-customer-reviews').scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' });
      }
    });
    on(document, 'click', (event) => { if (event.target.closest('[data-af-story-fit]')) findFit(event); });
    on(window, 'scroll', schedule, { passive: true });
    on(window, 'resize', schedule, { passive: true });
    on(root, 'scroll', schedule, { capture: true, passive: true });
    on(root, 'carousel:change', schedule);
    on(document, 'arcticfur:fit-change', schedule);
    on(document, 'variant:change', schedule);
    on(document, 'product:rerender', () => { queueMicrotask(schedule); });
    // Partial rerenders replace variant selectors, prices and the entire sticky bar.
    const observer = new MutationObserver(schedule);
    observer.observe(section, { childList: true, subtree: true });
    // Judge.me may load its genuine rating after the product section has rendered.
    const reviewObserver = new MutationObserver(updateRating);
    const reviewSection = document.getElementById('af-customer-reviews')?.closest('.shopify-section');
    if (reviewSection) reviewObserver.observe(reviewSection, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-average-rating', 'data-number-of-reviews'] });
    on(document, 'shopify:section:load', updateRating);
    on(document, 'shopify:section:unload', (event) => {
      if (event.target === section) { observer.disconnect(); reviewObserver.disconnect(); abort.abort(); cancelAnimationFrame(raf); }
    });
    update();
  }
  const mount = (scope = document) => scope.querySelectorAll('[data-af-product]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
